// Material UI
import { Table, TableBody, TableRow, TableCell, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Utility
import { formatPrice } from 'utility/numbers';

import calculatorIcon from 'images/calculator-icon.png';

// Table only to be used for receipts and transaction details
export default function ReceiptTable(props) {
    const { pos, products, showTitle } = props;
    const {
        sub_total,
        discount_code,
        disc_order_total,
        products_discounted,
        tax_name,
        tax_on_products,
        our_fee_total,
        cc_fee_total,
        total,
        hostTotal,
        host_absorbs_b4t_fees,
        host_absorbs_cc_fees,
    } = props.orderDetails;

    const showHostAbsorbsCC = cc_fee_total > 0 && host_absorbs_cc_fees && pos !== true;

    const ccFeePassedOn = host_absorbs_b4t_fees === 1 && host_absorbs_cc_fees === 0;
    const ourFeePassedOn = host_absorbs_b4t_fees === 0 && host_absorbs_cc_fees === 1;
    const bothFeePassedOn = host_absorbs_b4t_fees === 0 && host_absorbs_cc_fees === 0;

    const title = (() => {
        if (ccFeePassedOn) {
            return 'HYBRID "CC FEE PASSED ON"';
        }

        if (ourFeePassedOn) {
            return 'HYBRID "OUR FEE PASSED ON"';
        }

        if (bothFeePassedOn) {
            return 'BOTH FEES "PASSED ON"';
        }

        return 'BOTH FEES "ALL-IN"';
    })();

    return (
        <Root taxOnProducts={tax_on_products}>
            {showTitle && (
                <TitleHeader>
                    <img src={calculatorIcon} alt="calculator-icon" width={16} />
                    <span>{title}</span>
                </TitleHeader>
            )}

            <Table sx={{ mb: 'auto' }}>
                <TableBody>
                    {products.map((p, i) => (
                        <TableRow key={i}>
                            <ListCell>
                                {p.product_name + ' x ' + p.qty}
                            </ListCell>
                            <ListCell align="right">
                                {formatPrice(p.price * p.qty)}
                            </ListCell>
                        </TableRow>
                    ))}
                    {tax_on_products || !host_absorbs_b4t_fees || !host_absorbs_cc_fees ? (
                        <TableRow style={{ borderTopStyle: 'dashed' }}>
                            <SubTotalCell>
                                <b>Sub Total</b>
                            </SubTotalCell>
                            <SubTotalCell align="right">
                                <b>{formatPrice(sub_total)}</b>
                            </SubTotalCell>
                        </TableRow>
                    ) : null}
                    {disc_order_total ? (
                        <TableRow>
                            <DiscountCell>
                                <Typography variant="body2">Discount ({discount_code})</Typography>
                                {typeof products_discounted === 'number' && (
                                    <Typography variant="caption">
                                        on {products_discounted}{' '}
                                        {products_discounted === 1 ? 'ticket' : 'tickets'}
                                    </Typography>
                                )}
                            </DiscountCell>
                            <DiscountCell align="right">
                                {'- ' + formatPrice(disc_order_total)}
                            </DiscountCell>
                        </TableRow>
                    ) : null}
                    {tax_on_products ? (
                        <TableRow>
                            <ListCell>{tax_name}</ListCell>
                            <ListCell align="right">
                                {formatPrice(tax_on_products)}
                            </ListCell>
                        </TableRow>
                    ) : null}
                    {!host_absorbs_b4t_fees ? (
                        <TableRow>
                            <ListCell>Convenience Fee</ListCell>
                            <ListCell align="right">
                                {formatPrice(our_fee_total)}
                            </ListCell>
                        </TableRow>
                    ) : null}
                    {!host_absorbs_cc_fees && cc_fee_total > 0 ? (
                        <TableRow>
                            <ListCell>Credit Card Fee</ListCell>
                            <ListCell align="right">
                                {formatPrice(cc_fee_total)}
                            </ListCell>
                        </TableRow>
                    ) : null}

                    <TableRow style={{ borderTopStyle: 'dashed' }}>
                        <TotalCell>
                            <b>Customer Paid</b>
                        </TotalCell>
                        <TotalCell align="right">
                            <b>{formatPrice(total)}</b>
                        </TotalCell>
                    </TableRow>
                </TableBody>
            </Table>

            <PayoutContainer>
                <Table>
                    <TableBody>
                        {host_absorbs_b4t_fees ? (
                            <TableRow>
                                <ListCell>
                                    {pos ? 'Processing Fee' : 'Convenience Fee'}
                                </ListCell>
                                <ListCell align="right">
                                    -{formatPrice(our_fee_total)}
                                </ListCell>
                            </TableRow>
                        ) : null}
                        {showHostAbsorbsCC ? (
                            <TableRow>
                                <ListCell>Credit Card Fee</ListCell>
                                <ListCell align="right">
                                    -{formatPrice(cc_fee_total)}
                                </ListCell>
                            </TableRow>
                        ) : null}
                        <TableRow>
                            <TotalCell>
                                <b>Your Payout</b>
                            </TotalCell>
                            <TotalCell align="right">
                                <b>{formatPrice(hostTotal)}</b>
                            </TotalCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </PayoutContainer>
        </Root>
    );
}

const Root = styled('div', {
    shouldForwardProp: (prop) => prop !== 'taxOnProducts',
})(({ taxOnProducts }) => ({
    height: taxOnProducts ? '310px' : '280px',
    display: 'flex',
    flexDirection: 'column',
}));

const ListCell = styled(TableCell)({
    border: 'none',
    paddingTop: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: '0.4rem',
});

const DiscountCell = styled(TableCell)(({ theme }) => ({
    border: 'none',
    paddingTop: 0,
    paddingLeft: 0,
    paddingRight: 0,
    color: theme.palette.success.main,
    verticalAlign: 'top',
}));

const SubTotalCell = styled(TableCell)({
    border: 'none',
    paddingTop: '0.4rem',
    paddingLeft: 0,
    paddingRight: 0,
});

const TotalCell = styled(TableCell)({
    border: 'none',
    paddingTop: '0.4rem',
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: 0,
});

const PayoutContainer = styled('div')({
    backgroundColor: 'rgb(240, 240, 240)',
    borderRadius: 4,
    padding: '0.5rem 0.25rem',
});

const TitleHeader = styled('div')({
    backgroundColor: 'rgb(240, 240, 240)',
    borderRadius: 4,
    padding: '0.25rem',
    marginBottom: '1.5rem',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
});
